<template>
  <div class="cal-range-picker">
    <!-- Popover calendar: closed until user clicks start / end (same pattern as calendarPicker.vue) -->
    <v-date-picker
      ref="picker"
      class="cal-range-picker__vc"
      :masks="masks"
      v-bind="$props"
      v-model="valueDate"
      is-range
      is24hr
      :columns="2"
      :popover="rangePopoverOptions"
    >
      <template v-slot="{ inputValue, inputEvents }">
        <div class="cal-range-picker__row">
          <div
            class="cal-range-picker__field cal-range-picker__field--trigger"
            v-on="inputEvents.start"
            role="button"
            tabindex="0"
          >
            <span class="cal-range-picker__icon"><i class="fa-regular fa-calendar-alt"/></span>
            <span class="cal-range-picker__date">{{ rangeLabelStart(inputValue) }}</span>
          </div>
          <span class="cal-range-picker__arrow"><i class="fa-solid fa-arrow-right"/></span>
          <div
            class="cal-range-picker__field cal-range-picker__field--trigger"
            v-on="inputEvents.end"
            role="button"
            tabindex="0"
          >
            <span class="cal-range-picker__icon"><i class="fa-regular fa-calendar-alt"/></span>
            <span class="cal-range-picker__date">{{ rangeLabelEnd(inputValue) }}</span>
          </div>
        </div>
      </template>
    </v-date-picker>
  </div>
</template>

<script>

export default {
  name: 'calendarPickerRange',
  data: () => ({
    masks: {
      L: 'yyyy-dd-MM',
      input: ['yyyy-dd-MM'],
      inputDateTime: ['yyyy-dd-MM HH:mm'],

      // ...optional `title`, `weekdays`, `navMonths`, etc
    }
  }),
  props: {
    value: {
      required: true,
      default: () => null
    },
    mode: {
      required: false,
      default: () => 'date'
    },
    size: {
      required: false,
      default: () => 'md'
    },
    minDate: {},
    maxDate: {},
    disabledDates: {},
    availableDates: {},
    clearable:{
      required: false,
      default: () => true
    },
    /** Popper placement, e.g. `bottom-end` when the picker sits in the last column (keeps calendar inside viewport). */
    popoverPlacement: {
      type: String,
      default: null,
    },
  },
  computed: {
    valueDate: {
      get() {
        if (typeof this.value === 'object' && this.value !== null) {
          return this.value
        } else {
          return {
            start: null,
            end: null
          }
        }
      },
      set(val) {
        if (val.start != null) {
          val.start = this.$moment(val.start)
              .format(this.i18nInputKey)
        }
        if (val.end != null) {
          val.end = this.$moment(val.end)
              .format(this.i18nInputKey)
        }
        this.$emit('input', val)
      }
    },
    formattedTime() {
      let result = {
        start: '',
        end: ''
      }
      let startTime = this.$moment(this.valueDate.start, this.i18nInputKey)
      let endTime = this.$moment(this.valueDate.end, this.i18nInputKey)
      if (startTime.isValid()) {
        result.start = startTime.format('DD.MM.YYYY'); // Format to yyyy-MM-dd
      }
      if (endTime.isValid()) {
        result.end = endTime.format('DD.MM.YYYY'); // Format to yyyy-MM-dd
      }
      return result
    },
    i18nMaskKey() {
      return this.msg('timeFormat.' + this.mode + '.mask')
    },
    i18nFormatKey() {
      return this.msg('timeFormat.' + this.mode + '.format')
    },
    i18nInputKey() {
      return this.mode === 'date' ? 'YYYY-MM-DD' : 'YYYY-MM-DD HH:mm'
    },
    /**
     * Popper: fixed strategy + flip/preventOverflow/shift so the panel stays inside the viewport
     * (e.g. deposit filters last column). Optional popoverPlacement for right-aligned triggers.
     */
    rangePopoverOptions() {
      const modifiers = [
        {
          name: 'flip',
          options: {
            padding: 12,
            rootBoundary: 'viewport',
            fallbackPlacements: ['top-start', 'top-end', 'bottom-end', 'bottom-start', 'left-start', 'right-start'],
          },
        },
        {
          name: 'preventOverflow',
          options: {
            padding: 12,
            altAxis: true,
            rootBoundary: 'viewport',
          },
        },
        {
          name: 'shift',
          options: {
            padding: 12,
            rootBoundary: 'viewport',
          },
        },
      ]
      const opts = {
        positionFixed: true,
        visibility: 'click',
        modifiers,
      }
      if (this.popoverPlacement) {
        opts.placement = this.popoverPlacement
      }
      return opts
    },
  },
  methods: {
    rangeLabelStart(inputValue) {
      if (inputValue && inputValue.start) return inputValue.start
      return this.formattedTime.start || this.msg('start')
    },
    rangeLabelEnd(inputValue) {
      if (inputValue && inputValue.end) return inputValue.end
      return this.formattedTime.end || this.msg('end')
    },
    setInput(val) {
      this.$emit('input', val)
    },
    clearInput() {
      this.$emit('input', null);
      this.forceUpdate();
    },
    setToToday() {
      let valueDate = {
        start: this.$moment().format(this.i18nInputKey),
        end: this.$moment().format(this.i18nInputKey)
      }
      this.valueDate = valueDate;
      this.forceUpdateValue(valueDate);
      this.moveCalendar(valueDate);
    },
    setThisMonth(){
      let valueDate = {
        start: this.$moment().startOf('month').format(this.i18nInputKey),
        end: this.$moment().format(this.i18nInputKey)
      }
      this.valueDate = valueDate;
      this.forceUpdateValue(valueDate);
      this.moveCalendar(valueDate);
    },
    setLastMonth(){
      let valueDate = {
        start: this.$moment().subtract(1, 'month').startOf('month').format(this.i18nInputKey),
        end: this.$moment().subtract(1, 'month').endOf('month').format(this.i18nInputKey)
      }

      this.valueDate = valueDate;
      this.moveCalendar(valueDate);
      this.forceUpdateValue(valueDate);
    },
    setDays(days){
      let valueDate = {
        start: this.$moment().subtract(days, 'days').format(this.i18nInputKey),
        end: this.$moment().format(this.i18nInputKey)
      }
      this.valueDate = valueDate;
      this.moveCalendar(valueDate);
      this.forceUpdateValue(valueDate);
    },
    setThisYear(){
      let valueDate = {
        start: this.$moment().startOf('year').format(this.i18nInputKey),
        end: this.$moment().format(this.i18nInputKey)
      }
      this.valueDate = valueDate;
      this.moveCalendar(valueDate);
      this.forceUpdateValue(valueDate);
    },
    forceUpdateValue(valueDate){
      this.$refs.picker.forceUpdateValue(valueDate);
    },
    moveCalendar(valueDate){
      let moment = this.$moment(valueDate.start,this.i18nInputKey);
      let month = parseInt(moment.format("M"));
      let year = parseInt(moment.format("YYYY"));
      this.$refs.picker.move({ month, year });
    }
  }
}
</script>

<style lang="scss" scoped>
.cal-range-picker { width: 100%; }
/* Selected range row */
.cal-range-picker__row { display: flex; align-items: center; gap: 10px; margin-bottom: 14px; }
.cal-range-picker__field {
  flex: 1; display: inline-flex; align-items: center;
  background: #f1f5f9; border: 1px solid #e2e8f0; border-radius: 10px; padding: 7px 12px; gap: 8px;
}
.cal-range-picker__field--trigger {
  cursor: pointer;
  transition: border-color .15s ease, box-shadow .15s ease;
}
.cal-range-picker__field--trigger:hover {
  border-color: #c4b5fd;
  box-shadow: 0 0 0 2px rgba(115, 103, 240, .12);
}
.cal-range-picker__field--trigger:focus {
  outline: none;
  border-color: #7367f0;
  box-shadow: 0 0 0 2px rgba(115, 103, 240, .2);
}
.cal-range-picker__icon { color: #7367f0; font-size: .9rem; flex-shrink: 0; }
.cal-range-picker__date { font-size: .875rem; font-weight: 600; color: #334155; }
.cal-range-picker__arrow { color: #94a3b8; font-size: .75rem; }
/* Popover anchor only — calendar renders in v-calendar popover */
.cal-range-picker__vc { width: 100% !important; border: none !important; background: transparent !important; }
</style>
<style lang="scss">
/*
 * Do NOT use `.cal-range-picker .vc-container { background: transparent }` — v-date-picker reuses
 * `vc-container` on the *popover* panel, so that rule made the floating calendar fully transparent.
 */
.cal-range-picker .vc-pane-container { width: 100% !important; }
.cal-range-picker .vc-pane { flex: 1; }
.cal-range-picker .vc-highlight { background: #7367f0 !important; border-radius: 8px !important; }
.cal-range-picker .vc-highlight-content-light { background: rgba(115,103,240,.15) !important; border-radius: 8px !important; }
.cal-range-picker .vc-day-content:hover { background: rgba(115,103,240,.12) !important; border-radius: 8px !important; }
.cal-range-picker .vc-title { font-weight: 700 !important; font-size: .92rem !important; }
.cal-range-picker .vc-weekday { font-weight: 700; font-size: .72rem; color: #94a3b8; text-transform: uppercase; }

/* Popover calendar panel (same node: .vc-popover-content.vc-container) — light */
.cal-range-picker .vc-popover-content.vc-container:not(.vc-is-dark) {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 12px !important;
  box-shadow: 0 12px 40px rgba(15, 23, 42, 0.12) !important;
}

/* v-calendar dark theme class (when app uses v-calendar dark) */
.cal-range-picker .vc-popover-content.vc-container.vc-is-dark {
  background: #283046 !important;
  border: 1px solid #3b4253 !important;
  border-radius: 12px !important;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.45) !important;
  color: #e2e8f0 !important;
}

/* CRM body.dark-layout — match dark theme + inner chrome */
.dark-layout .cal-range-picker__field { background: rgba(255,255,255,.06); border-color: rgba(255,255,255,.1); }
.dark-layout .cal-range-picker__field--trigger:hover { border-color: rgba(167, 139, 250, .45); box-shadow: 0 0 0 2px rgba(115, 103, 240, .15); }
.dark-layout .cal-range-picker__field--trigger:focus { border-color: #a78bfa; box-shadow: 0 0 0 2px rgba(167, 139, 250, .25); }
.dark-layout .cal-range-picker__icon { color: #a78bfa; }
.dark-layout .cal-range-picker__date { color: #e2e8f0; }
.dark-layout .cal-range-picker__arrow { color: #64748b; }

.dark-layout .cal-range-picker .vc-popover-content.vc-container {
  background: #283046 !important;
  border: 1px solid #3b4253 !important;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.45) !important;
  color: #e2e8f0 !important;
}
.dark-layout .cal-range-picker .vc-header,
.dark-layout .cal-range-picker .vc-nav-header,
.dark-layout .cal-range-picker .vc-weeks {
  background: transparent !important;
}
.dark-layout .cal-range-picker .vc-pane {
  border-color: rgba(255, 255, 255, 0.08) !important;
}
.dark-layout .cal-range-picker .vc-pane + .vc-pane {
  border-left: 1px solid rgba(255, 255, 255, 0.08) !important;
}
.dark-layout .cal-range-picker .vc-title { color: #e2e8f0 !important; }
.dark-layout .cal-range-picker .vc-arrow { color: #94a3b8 !important; }
.dark-layout .cal-range-picker .vc-arrow:hover { color: #e2e8f0 !important; }
.dark-layout .cal-range-picker .vc-weekday { color: #94a3b8 !important; }
.dark-layout .cal-range-picker .vc-day-content { color: #cbd5e1 !important; }
.dark-layout .cal-range-picker .vc-day-content:hover { background: rgba(115,103,240,.25) !important; color: #fff !important; }
.dark-layout .cal-range-picker .is-today .vc-day-content { color: #a78bfa !important; font-weight: 700; }
.dark-layout .cal-range-picker .vc-highlight-content-solid { color: #fff !important; }
.dark-layout .cal-range-picker .vc-highlight-content-light { color: #a78bfa !important; }
.dark-layout .cal-range-picker .vc-day-layer.vc-day-box-center-center:not(.is-not-in-month) .vc-day-content {
  color: #cbd5e1 !important;
}
.dark-layout .cal-range-picker .vc-monthly .is-not-in-month .vc-day-content { opacity: 0.35; }

/* Mirror dark popover content when v-calendar sets vc-is-dark (outside .dark-layout or together) */
.cal-range-picker .vc-popover-content.vc-container.vc-is-dark .vc-title { color: #e2e8f0 !important; }
.cal-range-picker .vc-popover-content.vc-container.vc-is-dark .vc-arrow { color: #94a3b8 !important; }
.cal-range-picker .vc-popover-content.vc-container.vc-is-dark .vc-weekday { color: #94a3b8 !important; }
.cal-range-picker .vc-popover-content.vc-container.vc-is-dark .vc-day-content { color: #cbd5e1 !important; }
.cal-range-picker .vc-popover-content.vc-container.vc-is-dark .vc-pane + .vc-pane {
  border-left: 1px solid rgba(255, 255, 255, 0.08) !important;
}

/* Day numbers were inheriting a muted grey — make them readable, keep out-of-month days faded */
.cal-range-picker .vc-day-content { color: #1f2a44 !important; font-weight: 600; }
.cal-range-picker .is-not-in-month .vc-day-content { opacity: .3; }
.cal-range-picker .vc-day.is-today .vc-day-content { color: #7367f0 !important; font-weight: 800; }
.cal-range-picker .vc-highlight-content-solid, .cal-range-picker .vc-highlight-content-outline { color: #fff !important; }
.cal-range-picker .vc-title { cursor: pointer; }
.cal-range-picker .vc-title:hover { color: #7367f0 !important; }

/* Month / year picker (click on the title): it rendered transparent, so the years were invisible */
.cal-range-picker .vc-nav-popover-container {
  background: #ffffff !important; color: #1f2a44 !important;
  border: 1px solid #e2e8f0 !important; border-radius: 12px !important;
  box-shadow: 0 12px 40px rgba(15, 23, 42, .16) !important; padding: 6px !important;
}
.cal-range-picker .vc-nav-header { margin-bottom: 4px; }
.cal-range-picker .vc-nav-title { color: #1f2a44 !important; font-weight: 800 !important; font-size: .9rem !important; }
.cal-range-picker .vc-nav-title:hover, .cal-range-picker .vc-nav-arrow:hover { background: rgba(115, 103, 240, .12) !important; color: #7367f0 !important; }
.cal-range-picker .vc-nav-arrow { color: #64748b !important; }
.cal-range-picker .vc-nav-item { color: #334155 !important; font-weight: 700 !important; border-radius: 8px !important; }
.cal-range-picker .vc-nav-item:hover { background: rgba(115, 103, 240, .12) !important; color: #7367f0 !important; }
.cal-range-picker .vc-nav-item.is-active { background: #7367f0 !important; color: #fff !important; box-shadow: none !important; }
.cal-range-picker .vc-nav-item.is-current { color: #7367f0 !important; border-color: #7367f0 !important; }
.dark-layout .cal-range-picker .vc-nav-popover-container { background: #161d31 !important; color: #e2e8f0 !important; border-color: #3b4253 !important; }
.dark-layout .cal-range-picker .vc-nav-title { color: #e2e8f0 !important; }
.dark-layout .cal-range-picker .vc-nav-item { color: #cbd5e1 !important; }
.dark-layout .cal-range-picker .vc-nav-item.is-active { background: #7367f0 !important; color: #fff !important; }

/* v-calendar popover: default z-index (10) sits under sticky headers / cards */
.vc-popover-content-wrapper {
  z-index: 20050 !important;
}
</style>
